import React, { useEffect, useState } from "react";
import {
  CoreContainer,
  FullViewContainer,
  Notification,
} from "../common/style";
import {
  Input,
  Row,
  UncontrolledTooltip,
  FormGroup,
  Col,
  Label,
  Button,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Spinner,
} from "reactstrap";
import {
  ProjectImgContainer,
  ProjectProductInfo,
  DimensionSpan,
  ExpoIcon,
  OrderItemContainer,
} from "../project/style";
import { toLower } from "lodash";
import { addProducts, getOrderItemCutlist } from "../product/service";
import CrudModal from "../project/CrudModal";
import { addUserRoom } from "../project/service";
import { CatalogsModal } from "../product";
import { EXPO_PROPERTY_CONSTANTS, EXPO_SIDE_IDS } from "../constants";
import Cutlist from "./Cutlist";
import MobileCutlist from "./MobileCutlist";

const CarpenterView = ({ project, loading, defaultRoom }) => {
  const [currentRoom, setCurrentRoom] = useState({ ...defaultRoom });
  const [currentGroup, setCurrentGroup] = useState(
    currentRoom.groups.length ? currentRoom.groups[0] : null
  );
  const [isLoading, setIsLoading] = useState(loading);
  const [currentGroupOrderItems, setCurrentGroupOrderItems] = useState({});
  const [orderItemsLoaded, setOrderItemsLoaded] = useState(false);
  const [cutlistCanvas, setCutlistCanvas] = useState(false);
  const [notification, setNotification] = useState({ type: "", message: "" });
  const [isNotificationVisible, setIsNotificationVisible] = useState(true);
  const [orderItemCutlists, setOrderItemCutlists] = useState([]);
  const [addRoomModal, setAddRoomModal] = useState(false);
  const [catalogsModal, setCatalogsModal] = useState(false);

  useEffect(() => {
    setCurrentRoom(project.rooms.find((room) => room.id === currentRoom.id));
  }, [project]);

  useEffect(() => {
    setCurrentGroup(currentRoom.groups.length ? currentRoom.groups[0] : null);
  }, [currentRoom]);

  useEffect(() => {
    if (currentGroup.order_items && currentGroup.order_items.length) {
      const orderItems = currentGroup.order_items.reduce(
        (accumulator, value) => {
          accumulator[value.id] = {
            dimensions: value.customized_product.dimensions,
            expoSides: value.customized_product.exposed_sides,
            name: value.name,
            image: value.images[0],
            selected_options: value.customized_product.selected_options,
          };

          return accumulator;
        },
        {}
      );

      setCurrentGroupOrderItems({ ...orderItems });
    }
  }, [currentGroup]);

  useEffect(() => {
    setOrderItemsLoaded(true);
  }, [currentGroupOrderItems]);

  const onDismissNotification = () => {
    setIsNotificationVisible(false);
  };

  const toggleAddRoom = () => setAddRoomModal(!addRoomModal);
  const toggleCatalogs = () => setCatalogsModal(!catalogsModal);

  const handleOrderItemDimensionsChange = (orderItemId, dimension, value) => {
    setCurrentGroupOrderItems({
      ...currentGroupOrderItems,
      [orderItemId]: {
        ...currentGroupOrderItems[orderItemId],
        dimensions: {
          ...currentGroupOrderItems[orderItemId].dimensions,
          [dimension]: value,
        },
      },
    });
  };

  const handleOrderItemExpoChange = (orderItemId, selectedOptions) => {
    setCurrentGroupOrderItems({
      ...currentGroupOrderItems,
      [orderItemId]: {
        ...currentGroupOrderItems[orderItemId],
        expoSides: Array.from(selectedOptions, (option) => option.value),
      },
    });
  };

  const handleExpoSidesSelect = (orderItemId, value) => {
    setCurrentGroupOrderItems({
      ...currentGroupOrderItems,
      [orderItemId]: {
        ...currentGroupOrderItems[orderItemId],
        openExpoSides: value,
      },
    });
  };

  const updateOrderItemForGeneratingCutlist = ({
    orderItemId,
    groupId,
    openCutlistModal,
  }) => {
    const exposedSides =
      currentGroupOrderItems[orderItemId].expoSides.map(toLower);

    const exposedSidesPayload = Object.keys(EXPO_PROPERTY_CONSTANTS).reduce(
      (accumulator, value) => {
        accumulator[`exposed_side_${value}`] = exposedSides.includes(value)
          ? EXPO_PROPERTY_CONSTANTS[value][0]
          : EXPO_PROPERTY_CONSTANTS[value][1];

        return accumulator;
      },
      {}
    );

    setIsLoading(true);

    if (openCutlistModal) {
      setCutlistCanvas(true);
    }

    const payload = {
      ...currentGroupOrderItems[orderItemId].dimensions,
      ...exposedSidesPayload,
    };

    return getOrderItemCutlist({
      orderItemId,
      projectId: project.id,
      groupId,
      options: { ...payload },
    })
      .then((cutlists) => {
        setOrderItemCutlists(Object.values(cutlists).flat());

        if (!openCutlistModal) {
          setIsNotificationVisible(true);
          setNotification({
            type: "success",
            message: "Saved Successfully",
          });
        }
      })
      .catch(({ error }) => {
        setIsNotificationVisible(true);
        setNotification({ type: "error", message: error });
      })
      .finally(() => setIsLoading(false));
  };

  const addRoom = ({ projectId, payload }) => {
    return addUserRoom({ projectId, payload: { ...payload, add_group: true } })
      .then((_) => {
        setIsNotificationVisible(true);
        setNotification({
          type: "success",
          message: "Room Added Successfully",
        });
      })
      .catch(({ error }) => {
        setIsNotificationVisible(true);
        setNotification({ type: "error", message: error });
      });
  };

  const handleAddItem = (itemId) => {
    setIsLoading(true);

    return addProducts({
      productId: itemId,
      projectId: project.id,
      selectedGroupId: currentGroup.id,
      selectedRoomId: currentRoom.id,
    })
      .then((_) => {
        setIsNotificationVisible(true);
        setNotification({
          type: "success",
          message: "Item Added Successfully",
        });
      })
      .catch(({ error }) => {
        setIsNotificationVisible(true);
        setNotification({ type: "error", message: error });
      })
      .finally(() => {
        setIsLoading(false);
        toggleCatalogs();
      });
  };

  return (
    <div>
      {notification.type && (
        <Notification
          color={notification.type === "error" ? "danger" : "success"}
          isOpen={isNotificationVisible}
          toggle={onDismissNotification}
        >
          {notification.message}
        </Notification>
      )}
      <Offcanvas
        className="bottom-offcanvas"
        isOpen={cutlistCanvas}
        direction="bottom"
      >
        <OffcanvasHeader toggle={() => setCutlistCanvas(!cutlistCanvas)}>
          {`Cutlists`}
        </OffcanvasHeader>
        <OffcanvasBody>
          {isLoading ? (
            <CoreContainer height={"30vh"}>
              <Spinner>Loading...</Spinner>
            </CoreContainer>
          ) : (
            orderItemCutlists.map((e, index) => (
              <MobileCutlist
                showcolor={true}
                cutlist={e}
                index={index}
                key={index}
              />
            ))
          )}
        </OffcanvasBody>
      </Offcanvas>
      {project ? (
        <FullViewContainer overflow="scroll">
          <div className="p-2 ps-0">
            <Button onClick={toggleAddRoom} outline size="sm">
              <i className="fa-solid fa-circle-plus me-2"></i>
              Add Room
            </Button>
          </div>
          <CrudModal
            isOpen={addRoomModal}
            toggle={toggleAddRoom}
            crudAction={(projectId, _, payload) =>
              addRoom({ projectId, payload })
            }
            id={project.id}
            isRoomAndGroup={true}
            text="Add Room & Group"
          />
          <CatalogsModal
            isOpen={catalogsModal}
            toggle={toggleCatalogs}
            handleAddItem={handleAddItem}
            loading={isLoading}
          />
          <Row className="mb-4">
            <div className="col-6 d-flex align-items-center ps-0">
              <Input
                className="py-1 form-select"
                name="select"
                type="select"
                value={currentRoom.id}
                onChange={(e) => {
                  const currentRoom = project.rooms.find(
                    (room) => room.id == e.target.value
                  );
                  setCurrentRoom(currentRoom);
                }}
              >
                {project.rooms.map((room, index) => (
                  <option key={index} value={room.id}>
                    {room.name}
                  </option>
                ))}
              </Input>
            </div>
            <div className="col-6 d-flex align-items-center pe-0">
              <Input
                className="py-1 form-select"
                name="select"
                type="select"
                value={currentGroup.id}
                onChange={(e) => {
                  const currentGroup = currentRoom.groups.find(
                    (group) => group.id == e.target.value
                  );
                  setCurrentGroup(currentGroup);
                }}
              >
                {currentRoom.groups.map((group, index) => (
                  <option key={index} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </Input>
            </div>
          </Row>
          {currentGroup.order_items && currentGroup.order_items.length ? (
            <>
              {orderItemsLoaded &&
                Object.keys(currentGroupOrderItems).map(
                  (orderItemId, index) => {
                    return (
                      <OrderItemContainer
                        className={`row align-items-center px-3 py-1 border-bottom`}
                        key={index}
                      >
                        <ProjectImgContainer className="col-2 px-0 d-flex justify-content-center align-items-center">
                          <img
                            className="mh-100 mw-100 pointer"
                            src={currentGroupOrderItems[orderItemId].image}
                          />
                        </ProjectImgContainer>
                        <div className="col-10 ps-4 pe-0 pointer">
                          <ProjectProductInfo
                            line="2"
                            className="m-0"
                            title={currentGroupOrderItems[orderItemId].name}
                          >
                            {currentGroupOrderItems[orderItemId].name}
                          </ProjectProductInfo>
                          <ProjectProductInfo color="gray" className={`m-0 `}>
                            <div className="d-flex align-items-center">
                              <DimensionSpan>
                                <Input
                                  type="number"
                                  pattern="[0-9]*"
                                  inputMode="numeric"
                                  onChange={(e) =>
                                    handleOrderItemDimensionsChange(
                                      orderItemId,
                                      "height",
                                      e.target.value
                                    )
                                  }
                                  value={
                                    currentGroupOrderItems[orderItemId]
                                      .dimensions.height
                                  }
                                />
                                <span>H</span>
                              </DimensionSpan>
                              <DimensionSpan>
                                <Input
                                  type="number"
                                  pattern="[0-9]*"
                                  inputMode="numeric"
                                  onChange={(e) =>
                                    handleOrderItemDimensionsChange(
                                      orderItemId,
                                      "width",
                                      e.target.value
                                    )
                                  }
                                  value={
                                    currentGroupOrderItems[orderItemId]
                                      .dimensions.width
                                  }
                                />
                                <span>W</span>
                              </DimensionSpan>
                              <DimensionSpan>
                                <Input
                                  type="number"
                                  pattern="[0-9]*"
                                  inputMode="numeric"
                                  onChange={(e) =>
                                    handleOrderItemDimensionsChange(
                                      orderItemId,
                                      "depth",
                                      e.target.value
                                    )
                                  }
                                  value={
                                    currentGroupOrderItems[orderItemId]
                                      .dimensions.depth
                                  }
                                />
                                <span>D</span>
                              </DimensionSpan>
                            </div>
                            <div className="mt-1">
                              <div className="d-flex align-items-center">
                                <div className="w-100 rounded-1 btn-group me-3">
                                  {
                                    <FormGroup row className="w-100">
                                      <div className="ps-0">
                                        {EXPO_SIDE_IDS.find(
                                          (side) =>
                                            currentGroupOrderItems[orderItemId]
                                              .selected_options[side]
                                        ) && (
                                          <Label
                                            for="expoMultiSelect"
                                            className="ps-0"
                                            sm={2}
                                          >
                                            <React.Fragment>
                                              <ExpoIcon
                                                onClick={() =>
                                                  handleExpoSidesSelect(
                                                    orderItemId,
                                                    true
                                                  )
                                                }
                                                id={`expo_${index}`}
                                                className={currentGroupOrderItems[
                                                  orderItemId
                                                ].expoSides
                                                  .filter(Boolean)
                                                  .join(" ")}
                                              >
                                                E
                                              </ExpoIcon>
                                              <UncontrolledTooltip
                                                style={{
                                                  fontSize: "16px",
                                                  padding: "6px 20px",
                                                }}
                                                target={`expo_${index}`}
                                                placement="right"
                                              >
                                                {currentGroupOrderItems[
                                                  orderItemId
                                                ].expoSides.some(Boolean)
                                                  ? " Expo: "
                                                  : "No Expo Sides"}
                                                {currentGroupOrderItems[
                                                  orderItemId
                                                ].expoSides
                                                  .filter(Boolean)
                                                  .join(", ")}
                                              </UncontrolledTooltip>
                                            </React.Fragment>
                                          </Label>
                                        )}
                                        <Button
                                          onClick={() =>
                                            updateOrderItemForGeneratingCutlist(
                                              {
                                                orderItemId,
                                                groupId: currentGroup.id,
                                                openCutlistModal: false,
                                              }
                                            )
                                          }
                                          color="link"
                                        >
                                          Save
                                        </Button>
                                        <Button
                                          style={{ marginLeft: "-15px" }}
                                          onClick={() =>
                                            updateOrderItemForGeneratingCutlist(
                                              {
                                                orderItemId,
                                                groupId: currentGroup.id,
                                                openCutlistModal: true,
                                              }
                                            )
                                          }
                                          color="link"
                                        >
                                          Cutlist
                                        </Button>
                                      </div>

                                      {currentGroupOrderItems[orderItemId]
                                        .openExpoSides && (
                                        <Col md={12} className="ps-0">
                                          <Input
                                            id="expoMultiSelect"
                                            multiple
                                            name="expoMultiSelect"
                                            type="select"
                                            value={
                                              currentGroupOrderItems[
                                                orderItemId
                                              ].expoSides
                                            }
                                            onChange={(e) =>
                                              handleOrderItemExpoChange(
                                                orderItemId,
                                                e.target.selectedOptions
                                              )
                                            }
                                            onBlur={() =>
                                              handleExpoSidesSelect(
                                                orderItemId,
                                                false
                                              )
                                            }
                                          >
                                            {Object.keys(
                                              EXPO_PROPERTY_CONSTANTS
                                            ).map((side, index) => {
                                              return (
                                                <option
                                                  value={
                                                    side
                                                      .charAt(0)
                                                      .toUpperCase() +
                                                    side.slice(1)
                                                  }
                                                  index={index}
                                                >
                                                  {side
                                                    .charAt(0)
                                                    .toUpperCase() +
                                                    side.slice(1)}
                                                </option>
                                              );
                                            })}
                                          </Input>
                                        </Col>
                                      )}
                                    </FormGroup>
                                  }
                                </div>
                              </div>
                            </div>
                          </ProjectProductInfo>
                        </div>
                      </OrderItemContainer>
                    );
                  }
                )}
            </>
          ) : (
            <CoreContainer height="unset">
              <span>No Order Items</span>
            </CoreContainer>
          )}
          <div className="p-2 ps-0">
            <Button onClick={toggleCatalogs} outline size="sm">
              <i className="fa-solid fa-circle-plus me-2"></i>
              Add Item
            </Button>
          </div>
        </FullViewContainer>
      ) : (
        <CoreContainer>
          {isLoading ? (
            <CoreContainer>
              <Spinner>Loading...</Spinner>
            </CoreContainer>
          ) : (
            <span> No Projects Available!</span>
          )}
        </CoreContainer>
      )}
    </div>
  );
};

export default CarpenterView;
